import React, { useMemo } from 'react';
import { Box, IconButton, ListItem, ListItemText, Stack } from '@mui/material';
import { Cancel, CheckCircle } from '@mui/icons-material';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import CustomFormControl from '../../../../../shared/atoms/CustomFormControl';
import CustomTooltip from '../../../../../shared/atoms/CustomTooltip';
import MoreOptionsMenu from '../../../../../shared/molecules/MoreOptionsMenu';

const BuildingItem = ({
  handleRemoveBuilding,
  handleCancelBuildingEdit,
  handleConfirmBuildingEdit,
  handleBuildingEdit,
  handleBuildingClone,
  building,
  isLast,
  isEditing,
  index,
  handleChange,
  handleBlur,
  errors,
  touched,
  disabled,
}) => {
  const { t } = useTranslation();

  const moreOptions = useMemo(() => {
    const options = [
      {
        label: t('components.location.edit'),
        action: () => handleBuildingEdit(building),
      },
      {
        label: t('components.location.clone'),
        action: () => handleBuildingClone(building),
      },
      {
        label: t('components.location.delete'),
        action: () => handleRemoveBuilding(building.id),
      },
    ];
    return options;
  }, [
    building,
    handleBuildingClone,
    handleBuildingEdit,
    handleRemoveBuilding,
    t,
  ]);

  return (
    <ListItem
      sx={{
        border: theme => `1px solid ${theme.palette.secondary.light}`,
        borderRadius: '6px',
        boxShadow:
          '0px 4px 6px rgba(0, 0, 0, 0.1), 0px 8px 16px rgba(0, 0, 0, 0.1)',
        transition: 'background-color 0.3s ease',
        ':hover': {
          backgroundColor: theme => theme.palette.background.default,
          color: theme => building.selected && theme.palette.text.primary,
        },
        backgroundColor: theme =>
          building.selected && theme.palette.background.paper,
        color: theme =>
          building.selected
            ? theme.palette.text.secondary
            : theme.palette.text.primary,
        cursor: 'pointer',
        marginBottom: isLast ? 0 : '10px',
      }}
      secondaryAction={
        !isEditing && (
          <MoreOptionsMenu
            options={moreOptions}
            disabled={disabled}
            size="small"
            margin="0 -14px 0 0"
            color="primary.main"
          />
        )
      }
      onClick={() => {
        if (building.lng && building.lat) {
          const pointProps = window.map
            .queryRenderedFeatures()
            .filter(
              item =>
                item.properties.user_id === building.id &&
                item.layer.id.includes('point-stroke'),
            )[0];
          if (pointProps)
            window.map.flyTo({
              center: pointProps.geometry.coordinates,
              zoom: 18,
            });
          else
            window.map.flyTo({
              center: [building.lng, building.lat],
              zoom: 18,
            });
        }
      }}
    >
      {isEditing ? (
        <Stack>
          <CustomFormControl
            id={`buildings.${index}.name`}
            label={t('tools_menu.location.name')}
            required
            onChange={handleChange}
            errors={errors}
            onBlur={handleBlur}
            value={building.name}
            touched={touched}
            noMarginTop
            maxLength={50}
          />
          <CustomFormControl
            id={`buildings.${index}.description`}
            label={t('tools_menu.location.description')}
            onChange={handleChange}
            errors={errors}
            onBlur={handleBlur}
            value={building.description}
            touched={touched}
            maxLength={255}
          />
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            paddingTop="5px"
            marginRight="-14px"
          >
            <CustomTooltip title={t('tools_menu.location.cancel')}>
              <IconButton
                color="primary"
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleCancelBuildingEdit();
                }}
              >
                <Cancel />
              </IconButton>
            </CustomTooltip>
            <CustomTooltip title={t('tools_menu.location.confirm')}>
              <Box>
                <IconButton
                  color="primary"
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleConfirmBuildingEdit(e);
                  }}
                  disabled={
                    !building.name || errors.buildings?.[index]?.name != null
                  }
                >
                  <CheckCircle />
                </IconButton>
              </Box>
            </CustomTooltip>
          </Stack>
        </Stack>
      ) : (
        <ListItemText
          primary={building.name}
          primaryTypographyProps={{
            variant: 'body1',
            fontWeight: 'bold',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            width: '180px',
          }}
          secondary={building.description}
          secondaryTypographyProps={{
            whiteSpace: 'normal',
            sx: {
              wordBreak: 'break-all',
              overflowWrap: 'break-word',
              color: null,
              width: '150px',
            },
          }}
        />
      )}
    </ListItem>
  );
};

BuildingItem.propTypes = {
  building: PropTypes.object.isRequired,
  isEditing: PropTypes.bool.isRequired,
  disabled: PropTypes.bool.isRequired,
  isLast: PropTypes.bool.isRequired,
  index: PropTypes.number.isRequired,
  handleBuildingEdit: PropTypes.func.isRequired,
  handleBuildingClone: PropTypes.func.isRequired,
  handleRemoveBuilding: PropTypes.func.isRequired,
  handleCancelBuildingEdit: PropTypes.func.isRequired,
  handleConfirmBuildingEdit: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  touched: PropTypes.object.isRequired,
};

export default BuildingItem;
